import { Component, inject } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { ThemePalette } from "@angular/material/core";
import { ActivatedRoute } from "@angular/router";
import { forkJoin, of } from "rxjs";
import { finalize, map, switchMap } from "rxjs/operators";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { FileHelper } from "src/app/helpers";
import { CstMailingService } from "src/app/mailing/mailing.service";
import { AlertService } from "src/app/services/alert.service";
import {
  CreateDokumentCommand,
  DokumentLookupDetailDto,
  InfoportalDokumenteClientProvider,
  RessourcenStatus,
  TagDto,
} from "src/app/web-api/web-api";

export interface IInfoportalDocumentCreateData {
  bezeichnung?: string;
  fileName?: string;
  facilityID?: number;
  fileMimeType?: string;
  fileData?: string;
  folderID?: string;
  folderSource?: "facility" | "contract" | "receipt";
  displayInternToggle?: boolean;
}

@Component({
    selector: "document-create",
    templateUrl: "./document-create.component.html",
    styleUrls: ["./document-create.component.scss"],
    standalone: false
})
export class InfoportalDocumentCreateComponent extends CstResponsivePopupComponentBase<
  IInfoportalDocumentCreateData,
  DokumentLookupDetailDto[]
> {
  private _fb = inject(FormBuilder);
  private _client = inject(InfoportalDokumenteClientProvider);
  private _route = inject(ActivatedRoute);
  private _mailService = inject(CstMailingService);
  private _alrt = inject(AlertService);

  displayInternToggle = false;
  folderSource: "facility" | "contract" | "receipt" = "facility";
  folderID = "";
  form: FormGroup;
  displayFolderSelection = true;
  public uploadUrl = "";
  public removeUrl = "";
  sliderColor: ThemePalette = "warn";
  loading = false;
  public userFiles: Array<File> = new Array<File>();

  constructor() {
    super();
    this.form = this._fb.group({
      id: null,
      vorgangID: [null],
      contractFolderID: [null],
      receiptFolderID: [null],
      bezeichnung: new FormControl("", Validators.required),
      fileData: [[], Validators.required],
      enableExternalAccess: new FormControl(false, Validators.required),
      status: RessourcenStatus.Aktiv,
      tagIDs: "",
    });
    this._route.queryParams.subscribe((params) => {
      if (params.folderSource) {
        this.folderSource = params.folderSource;
      }
      if (params.folderID) {
        this.displayFolderSelection = false;
        const val = {
          vorgangID: this.folderSource === "facility" ? params.folderID : null,
          contractFolderID:
            this.folderSource === "contract" ? params.folderID : null,
          receiptFolderID:
            this.folderSource === "receipt" ? params.folderID : null,
        };
        this.form.patchValue(val);
      }
    });
  }

  public onInitialised() {
    if (this.data?.bezeichnung) {
      this.form.patchValue({ bezeichnung: this.data.bezeichnung });
    }
    if (this.data?.fileData && this.data?.fileName) {
      const bstr = atob(this.data?.fileData);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      this.userFiles.push(
        new File([u8arr], this.data?.fileName, {
          type: this.data.fileMimeType || "application/octet-stream",
        }),
      );
      this.form.patchValue({ fileData: this.userFiles });
    }

    if (this.data?.folderSource) {
      this.folderSource = this.data.folderSource;
    }
    if (this.data?.folderID) {
      this.displayFolderSelection = false;
      const val = {
        vorgangID: this.folderSource === "facility" ? this.data.folderID : null,
        contractFolderID:
          this.folderSource === "contract" ? this.data.folderID : null,
        receiptFolderID:
          this.folderSource === "receipt" ? this.data.folderID : null,
      };
      this.form.patchValue(val);
    }
    this.displayInternToggle = this.data?.displayInternToggle;
  }

  public onCancel() {
    this.close();
  }

  public onAccept(event: MouseEvent) {
    (event.target as HTMLButtonElement).disabled = true;
    if (this.loading === true) {
      return;
    }
    this.loading = true;
    if (!this.form.valid) {
      this.loading = false;
      (event.target as HTMLButtonElement).disabled = true;
      return;
    }
    const value = this.form.value;
    if (this.form.controls.tagIDs.value === "") {
      this.form.controls.tagIDs.setValue(null);
    }

    of(value)
      .pipe(
        switchMap(() => {
          const filesConvert$ = this.userFiles.map((f) =>
            FileHelper.ConvertFileToBase64(f).pipe(
              map((base64) => ({
                name: f.name,
                base64,
              })),
            ),
          );
          return forkJoin(filesConvert$);
        }),
        switchMap((files) => {
          const requests$ = files.map((f) => {
            const cmd = new CreateDokumentCommand();
            cmd.bezeichnung = value.bezeichnung;
            cmd.intern = !value.enableExternalAccess;
            cmd.filename = f.name;
            cmd.fileData = f.base64;
            cmd.tagIDs = value.tagIDs;
            cmd.vorgangID = value.vorgangID;
            cmd.contractFolderID = value.contractFolderID;
            cmd.receiptFolderID = value.receiptFolderID;
            return this._client.documentCreate(cmd);
          });
          return forkJoin(requests$);
        }),
        switchMap((results) => {
          const firstWithMailData = results.find(
            (r) => r.documentVisibilityChangedMailData,
          );
          if (!firstWithMailData) {
            return of(results);
          }
          return this._mailService
            .displaySendMailDialog(
              firstWithMailData.documentVisibilityChangedMailData,
              {
                title: $localize`:@@Infoportal_DocumentCreateOrUpdate_SetExternalMailDialogTitle:Send mail to external users`,
                canBeDismissedByClickingOutsideDialogOrEscape: false,
              },
            )
            .pipe(map(() => results));
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe(
        (result) => {
          this._alrt
            .showSnackbar(
              $localize`:@@Infoportal_DocumentCreate_UploadSuccess:Upload was succesful`,
            )
            .subscribe();
          this.close(result);
        },
        (error) => {
          this._alrt
            .showSnackbar(
              $localize`:@@Infoportal_DocumentCreate_UploadFailure:Upload was not succesful`,
            )
            .subscribe();
        },
      );
  }

  public onTagSelection(event: Array<TagDto>) {
    const tagIdsFromEvent = Array.prototype.map.call(event, (e) => e.id);
    this.form.controls.tagIDs.setValue(tagIdsFromEvent);
  }

  public onFileSectionChanged(event: any): void {
    this.userFiles = Array.from(event);
    if (this.userFiles.length === 0) {
      this.form.controls.fileData.patchValue(null);
    } else {
      this.form.controls.fileData.patchValue(this.userFiles);
    }
    this.form.controls.fileData.markAsTouched();
    if (this.userFiles.length === 1 && !this.form.controls.bezeichnung.value) {
      this.form.controls.bezeichnung.setValue(this.userFiles[0].name);
    }
  }
}
